import { useCallback, useEffect, useState } from 'react';

import { Breadcrumbs, Switch, Text, Title, toastService } from '@thefarmersfront/kitchen';

import { registerMockAPI } from '~/mocks/mockApis';

import { Calendar, CalendarTopBar } from '~/components/common';
import { SettlementStorageDailyFeeDetailModal, StorageMonthlySummary } from '~/components/settlement';

import { useAPI, useModal, useStableCallback } from '~/hooks/common';
import { useStorageCalendar } from '~/hooks/settlement';
import { useLoadingStore } from '~/store';
import { formatDate, formatNumber } from '~/utils';

import { BREAD_CRUMB, PATH, TOAST_MESSAGE } from '~/constants';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypeGetStorageFeeMonthlyParams, TypeGetStorageFeeMonthlyResponse } from '~/types/settlement.types';

import styles from './SettlementStoragePage.module.scss';

const SettlementStoragePage = () => {
  const { isLoading, setIsLoading } = useLoadingStore();

  const { currentDate, days, goToPrevMonth, goToNextMonth, isPrevMonthDisabled, isNextMonthDisabled } =
    useStorageCalendar();

  const storageDailyFeeDetailModal = useModal({
    onOpen: (date?: Date | null) => {
      if (!date) return;
      setSelectedDate(date);
    },
  });

  const [monthlyData, setMonthlyData] = useState<TypeGetStorageFeeMonthlyResponse['data'] | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showActualBilling, setShowActualBilling] = useState(false);

  const getStorageFeeMonthly = useAPI<TypeGetStorageFeeMonthlyParams, TypeGetStorageFeeMonthlyResponse>({
    endpoint: `/api-inventory-agg/v1/storage-fee/monthly`,
    method: 'get',
  });

  const fetchStorageFeeMonthly = async (yearMonth: string) => {
    try {
      setIsLoading(true);

      const { data } = await getStorageFeeMonthly({ params: { yearMonth: yearMonth } });
      setMonthlyData(data);
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFetchStorageFeeMonthly = useStableCallback(fetchStorageFeeMonthly);

  useEffect(() => {
    const formattedCurrentDate = formatDate(currentDate, 'YYYYMM');

    handleFetchStorageFeeMonthly(formattedCurrentDate ?? '');
  }, [currentDate, handleFetchStorageFeeMonthly]);

  const postStorageFeeDailySkusExcelDownload = useAPI({
    endpoint: '/api-inventory-agg/v1/storage-fee/daily/skus/excel-download',
    method: 'post',
    responseType: 'blob',
  });

  const fetchStorageFeeExcelDownload = async (date: Date) => {
    const downloadingToastId = toastService.addToast({ message: TOAST_MESSAGE.DOWNLOADING, persist: true });

    try {
      setIsLoading({ dimmed: false });

      const formattedDate = formatDate(date, 'YYYYMM') ?? '';
      await postStorageFeeDailySkusExcelDownload({ params: { yearMonth: formattedDate } });

      toastService.addToast({
        message: TOAST_MESSAGE.DOWNLOAD_SUCCESS,
      });
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      setIsLoading(false);
      toastService.removeToast(downloadingToastId);
    }
  };

  const openDetailModal = useStableCallback(storageDailyFeeDetailModal.handleOpenModal);

  const renderDay = useCallback(
    (date: Date | null, showActualBilling: boolean) => {
      const isToday = date?.toDateString() === new Date().toDateString();
      const data = monthlyData?.days[formatDate(date, 'YYYYMMDD') ?? ''];

      return (
        <button className={styles['calendar-day']} onClick={() => openDetailModal(date)}>
          <div className={isToday ? styles['today'] : ''}>
            <Text size="xlarge" color="tertiary" element="p" weight={isToday ? 'semi-bold' : 'regular'} align="left">
              {date?.getDate()}
            </Text>
          </div>
          <div className={styles['calendar-day-contents']}>
            {showActualBilling ? (
              <Text size="large" weight="semi-bold">
                {data?.chargedStorageFee ? formatNumber(data.chargedStorageFee) : ''}
              </Text>
            ) : (
              <>
                <Text size="large" weight="semi-bold">
                  {data?.totalStorageFee ? formatNumber(data.totalStorageFee) : ''}
                </Text>
                <Text size="large" weight="semi-bold" color="point">
                  {data?.unchargedStorageFee ? `-${formatNumber(data.unchargedStorageFee)}` : ''}
                </Text>
              </>
            )}
          </div>
        </button>
      );
    },
    [monthlyData?.days, openDetailModal]
  );

  return (
    <div className={styles['page-wrapper']}>
      <div className={styles['page-title']}>
        <div className={styles['title-left-field']}>
          <Title>보관비 조회</Title>
        </div>
        <Breadcrumbs items={BREAD_CRUMB[PATH.SETTLEMENT_STORAGE]} />
      </div>
      <div className={styles['page-description']}>
        <Text size="medium" color="tertiary" element="p">
          전일 보관비 내역은 금일 오전 5:00에 업데이트 됩니다.
        </Text>
      </div>
      <div className={styles['page-contents']}>
        <div className={styles['calendar-section']}>
          <CalendarTopBar
            currentDate={currentDate}
            isPrevDisabled={isPrevMonthDisabled}
            isNextDisabled={isNextMonthDisabled}
            onPrevMonth={goToPrevMonth}
            onNextMonth={goToNextMonth}
            rightField={
              <div className={styles['calendar-top-bar-right-field']}>
                <Switch
                  checked={showActualBilling}
                  label="실제 과금액으로 보기"
                  labelPlacement="left"
                  onChange={(e) => setShowActualBilling(e.target.checked)}
                />
              </div>
            }
          />
          <div className={styles['calendar']}>
            <Calendar days={days} renderDay={(date) => renderDay(date, showActualBilling)} />
          </div>
        </div>
        <div className={styles['monthly-summary-section']}>
          {monthlyData?.total && (
            <StorageMonthlySummary
              data={monthlyData.total}
              onDownloadExcel={async () => {
                if (isLoading) return;
                await fetchStorageFeeExcelDownload(currentDate);
              }}
            />
          )}
        </div>
      </div>
      {storageDailyFeeDetailModal.isOpen && selectedDate && (
        <SettlementStorageDailyFeeDetailModal
          date={selectedDate}
          onClose={storageDailyFeeDetailModal.handleCloseModal}
        />
      )}
    </div>
  );
};

export default SettlementStoragePage;

// 5/19(오늘) 기준 전일(5/18)까지만 집계된다.
const MOCK_CHARGED_DAYS = 18;

const storageFeeMonthlyResponse: TypeGetStorageFeeMonthlyResponse = {
  data: {
    days: Object.fromEntries(
      Array.from({ length: MOCK_CHARGED_DAYS }, (_, index) => {
        const day = index + 1;
        const totalStorageFee = 120000 + day * 3500;
        const unchargedStorageFee = day % 3 === 0 ? 15000 : 0;

        return [
          `202605${String(day).padStart(2, '0')}`,
          { totalStorageFee, unchargedStorageFee, chargedStorageFee: totalStorageFee - unchargedStorageFee },
        ];
      })
    ),
    total: {
      totalStorageFee: 2758500,
      unchargedStorageFee: { total: 90000, RANGE_001: 60000, RANGE_002: 30000 },
      chargedStorageFee: 2668500,
    },
  },
};

registerMockAPI<TypeGetStorageFeeMonthlyResponse>({
  method: 'get',
  endpoint: '/api-inventory-agg/v1/storage-fee/monthly',
  label: '정산/보관비조회/월별보관비',
  scenarios: [{ name: '정상 응답', response: storageFeeMonthlyResponse }],
});
